import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Form, Input, InputNumber, Select, Space, Typography, message } from 'antd';
import dayjs from 'dayjs';
import api from '../../services/api';
import { BUDGET_PERIOD_LABEL, renderBudgetMoney } from '../../pages/Expense/budgetConstants';

const QUARTER_OPTIONS = [1, 2, 3, 4].map((q) => ({ value: q, label: `Q${q}` }));
const CURRENT_YEAR = dayjs().year();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => ({
  value: y,
  label: `${y}年`
}));

function periodKeyToDayjs(periodType, periodKey) {
  if (!periodKey) return null;
  if (periodType === 'annual') {
    const year = parseInt(String(periodKey), 10);
    return Number.isFinite(year) ? dayjs(`${year}-01-01`) : null;
  }
  if (periodType === 'quarterly') {
    const match = String(periodKey).match(/^(\d{4})-Q([1-4])$/i);
    if (!match) return null;
    const month = (parseInt(match[2], 10) - 1) * 3;
    return dayjs(`${match[1]}-${String(month + 1).padStart(2, '0')}-01`);
  }
  if (periodType === 'monthly') {
    const parsed = dayjs(`${periodKey}-01`);
    return parsed.isValid() ? parsed : null;
  }
  return null;
}

function dayjsToPeriodKey(periodType, value) {
  if (!value || !dayjs.isDayjs(value)) return undefined;
  if (periodType === 'annual') return String(value.year());
  if (periodType === 'monthly') return value.format('YYYY-MM');
  return undefined;
}

function QuarterPeriodSelect({ value, onChange }) {
  const match = String(value || '').match(/^(\d{4})-Q([1-4])$/i);
  const year = match ? parseInt(match[1], 10) : CURRENT_YEAR;
  const quarter = match ? parseInt(match[2], 10) : undefined;

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        style={{ width: '45%' }}
        value={year}
        options={YEAR_OPTIONS}
        onChange={(nextYear) => {
          onChange?.(quarter ? `${nextYear}-Q${quarter}` : undefined);
        }}
      />
      <Select
        style={{ width: '55%' }}
        placeholder="选择季度"
        value={quarter}
        options={QUARTER_OPTIONS}
        onChange={(nextQuarter) => {
          onChange?.(`${year}-Q${nextQuarter}`);
        }}
      />
    </Space.Compact>
  );
}

function hasBudgetAdjustmentDraftValues(values = {}) {
  return Boolean(
    values.target_org_code
    || values.category_id
    || values.period_key
    || values.adjust_amount != null
    || String(values.reason || '').trim()
  );
}

export default function BudgetAdjustmentForm({ form, initialValues, onValuesChange }) {
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const prevCategoryIdRef = useRef(undefined);

  const targetOrgCode = Form.useWatch('target_org_code', form);
  const categoryId = Form.useWatch('category_id', form);
  const periodKey = Form.useWatch('period_key', form);

  const selectedCategory = useMemo(
    () => categories.find((item) => item.id === categoryId),
    [categories, categoryId]
  );

  // 退回重提场景：先回填已有 form_data，loadMeta 会因检测到已有值而不再套用默认组织
  useLayoutEffect(() => {
    if (!initialValues || !Object.keys(initialValues).length) return;
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const loadMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);
      const res = await api.get('/expense/budget-adjustments/meta');
      if (!res.data.success) return;
      const data = res.data.data || {};
      setOrgOptions(data.options || []);
      setCategories(data.categories || []);
      const existing = form.getFieldsValue(true);
      if (!hasBudgetAdjustmentDraftValues(existing)) {
        form.setFieldsValue({
          target_org_code: data.default_org_code,
          category_id: undefined,
          period_key: undefined,
          adjust_amount: undefined,
          reason: undefined
        });
        setBalance(null);
      }
    } catch (e) {
      setOrgOptions([]);
      setCategories([]);
      setBalance(null);
      message.error(e.response?.data?.message || '加载预算调整表单失败');
    } finally {
      setLoadingMeta(false);
    }
  }, [form]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    const prev = prevCategoryIdRef.current;
    if (prev !== undefined && prev !== categoryId) {
      form.setFieldValue('period_key', undefined);
      setBalance(null);
    }
    prevCategoryIdRef.current = categoryId;
  }, [categoryId, form]);

  useEffect(() => {
    setBalance(null);
    if (!targetOrgCode || !categoryId || !periodKey) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setBalanceLoading(true);
        const res = await api.get('/expense/budget-adjustments/balance', {
          params: {
            target_org_code: targetOrgCode,
            category_id: categoryId,
            period_key: periodKey
          }
        });
        if (!cancelled && res.data.success) setBalance(res.data.data);
      } catch {
        if (!cancelled) setBalance(null);
      } finally {
        if (!cancelled) setBalanceLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [targetOrgCode, categoryId, periodKey]);

  const periodFieldRules = [{ required: true, message: '请选择所属周期' }];

  const renderPeriodField = () => {
    if (!selectedCategory) {
      return (
        <Form.Item label="所属周期" required>
          <Select disabled placeholder="请先选择预算类别" />
        </Form.Item>
      );
    }

    const periodType = selectedCategory.period_type;

    if (periodType === 'annual') {
      return (
        <Form.Item
          name="period_key"
          label="所属周期"
          rules={periodFieldRules}
          getValueProps={(value) => ({ value: periodKeyToDayjs('annual', value) })}
          getValueFromEvent={(date) => dayjsToPeriodKey('annual', date)}
        >
          <DatePicker picker="year" style={{ width: '100%' }} placeholder="请选择年度" />
        </Form.Item>
      );
    }

    if (periodType === 'quarterly') {
      return (
        <Form.Item name="period_key" label="所属周期" rules={periodFieldRules}>
          <QuarterPeriodSelect />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        name="period_key"
        label="所属周期"
        rules={periodFieldRules}
        getValueProps={(value) => ({ value: periodKeyToDayjs('monthly', value) })}
        getValueFromEvent={(date) => dayjsToPeriodKey('monthly', date)}
      >
        <DatePicker picker="month" style={{ width: '100%' }} placeholder="请选择月份" />
      </Form.Item>
    );
  };

  return (
    <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
      <Form.Item name="target_org_code" label="预算归属组织" rules={[{ required: true, message: '请选择预算归属组织' }]}>
        <Select
          loading={loadingMeta}
          placeholder="选择预算归属组织"
          options={orgOptions.map((item) => ({
            value: item.org_code,
            label: item.org_name
          }))}
        />
      </Form.Item>
      <Form.Item name="category_id" label="预算类别" rules={[{ required: true, message: '请选择类别' }]}>
        <Select
          loading={loadingMeta}
          placeholder="选择预算类别"
          showSearch
          optionFilterProp="label"
          options={categories.map((item) => ({
            value: item.id,
            label: `${item.name}（${item.period_type_label || BUDGET_PERIOD_LABEL[item.period_type] || item.period_type}）`
          }))}
        />
      </Form.Item>
      {renderPeriodField()}
      <Form.Item label="剩余预算">
        <Typography.Text type={balanceLoading ? 'secondary' : undefined}>
          {balanceLoading
            ? '查询中...'
            : balance
              ? renderBudgetMoney(balance.amount_available)
              : (targetOrgCode && categoryId && periodKey ? '—' : '请先选择组织、类别和周期')}
        </Typography.Text>
      </Form.Item>
      <Form.Item name="adjust_amount" label="调增金额" rules={[{ required: true, message: '请填写调增金额' }]}>
        <InputNumber min={0.01} precision={2} style={{ width: '100%' }} addonBefore="¥" />
      </Form.Item>
      <Form.Item name="reason" label="申请原因" rules={[{ required: true, message: '请填写申请原因' }]}>
        <Input.TextArea rows={4} maxLength={500} showCount placeholder="请说明调增原因" />
      </Form.Item>
    </Form>
  );
}
